import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import {
	MotifModal,
	MotifModalBody,
	MotifModalHeader,
	MotifFileUploader,
	MotifFileUploaderItem,
	MotifIcon,
	MotifModalFooter,
	MotifButton,
	MotifFormField,
} from "@ey-xd/motif-react";
import { IconoirBrightStar } from "@ey-xd/motif-icon";
import { uploadTransactionCSV } from "../../../store/slices/ConsumptionSlice";
import {CONSUMPTION_CSV_UPLOAD_ERROR_MESSAGE} from "../../../Constant";
import "./ConsumptionImport.scss";

interface ConsumptionImportProps {
	showUploadModal: boolean;
	setShowUploadModal: (show: boolean) => void;
	setIsProcessed: (processed: boolean) => void;
	setTransactionSummaryData: (data: any) => void;
}

const ConsumptionImport = ({
	showUploadModal,
	setShowUploadModal,
	setIsProcessed,
	setTransactionSummaryData,
}: ConsumptionImportProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const [file, setFile] = useState<File | null>(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [isUploadSuccess, setIsUploadSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>("");

	const handleFileChange = async(files: any) => {
		const selectedFile = files[0];
		setFile(selectedFile);
		if (!selectedFile) return;
		const validationResult = await fileValidator(selectedFile) as { message?: string } | null;
		if (validationResult) {
			setErrorMessage(validationResult.message ?? "");
		} else {
			setErrorMessage("");
		}
	};

	const handleUpload = async () => {
		if (file) {
			setIsUploadSuccess(false);
			setErrorMessage("");
			try {
				setIsUploading(true);
				const response = await dispatch(
					uploadTransactionCSV({
						file,
						onProgress: (percent: number) => setUploadProgress(percent),
					})
				);
				if (response.payload.Status === "success") {
					closeImportModal();
					setIsUploadSuccess(true);
					setIsProcessed(true);
					setTransactionSummaryData(response.payload.Data);
				} else {
					setUploadProgress(0);
					setErrorMessage(response.payload?.message || CONSUMPTION_CSV_UPLOAD_ERROR_MESSAGE);
				}
			} catch (error: any) {
				setUploadProgress(0);
				setErrorMessage(CONSUMPTION_CSV_UPLOAD_ERROR_MESSAGE);
			} finally {
				setIsUploading(false);
			}
		}
	};

	const handleFileRemove = () => {
		setFile(null);
		setUploadProgress(0);
		setErrorMessage("");
	};

	const fileValidator = (file: File) => {
		 const maxSize = 11 * 1024 * 1024;
		 if (file.size === 0) {
			return {
				code: "zero-byte-file",
				message: "The uploaded file is empty. Please upload a file with data.",
			};
			}
		 if (file.size > maxSize) {	
			return {
				code: "max-file-size-exceeded",
				message: "File size must be less than or equal to 11 MB.",
			};
		}

		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const text = e.target?.result as string;
				if (
					!text ||
					text.trim() === "" ||
					text
						.split(/\r?\n/)
						.every((line) => line.trim() === "" || /^,+$/.test(line.trim()))
				) {
					resolve({
						code: "empty-file",
						message:
							"The uploaded file is empty. Please upload a file with data.",
					});
				} else {
					resolve(null);
				}
			};
			reader.onerror = () => {
				resolve({
					code: "file-read-error",
					message: "Failed to read the file. Please try again.",
				});
			};
			reader.readAsText(file);
		});
	};

	const closeImportModal = () => {
		setShowUploadModal(false);
		document.body.style.overflow = "";
	}

	return (
    <MotifModal
      onClose={closeImportModal}
      show={showUploadModal}
      size="md"
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
      className="consumption-import-modal"
    >
      <MotifModalHeader
        closeButtonProps={{
          "aria-label": "Custom Close Button aria-label",
          title: "Custom Close Button title",
        }}
        className={isUploading || isUploadSuccess ? "import-modal-header" : ""}
      >
        <span>{uploadProgress > 0 ? "Uploading CSV" : "Upload CSV"}</span>
      </MotifModalHeader>
      <MotifModalBody className="import-modal-body">
        <MotifFormField>
          {isUploading || isUploadSuccess ? (
            <MotifFileUploaderItem
              fileIcon={<MotifIcon iconFunction={IconoirBrightStar} />}
              fileName={file ? file.name : ""}
              fileSize={file ? file.size : 0}
              percentUploaded={uploadProgress}
              showProgress={uploadProgress > 0}
              uploaded={uploadProgress === 100}
              uploadedText={""}
            />
          ) : (
            <MotifFileUploader
              id="consumption-import"
              label={"Click to upload or drag and drop"}
              labelDescription="The acceptable format is CSV only (maximum number of records is 65,000)"
              onDrop={handleFileChange}
              accept={{ "text/csv": [".csv"] }}
              maxFiles={1}
            >
              {file && (
                <MotifFileUploaderItem
                  fileIcon={
                    <MotifIcon iconFunction={IconoirBrightStar} size="20" />
                  }
                  fileName={file.name}
                  removable={true}
                  onRemove={handleFileRemove}
                  fileSize={file.size}
                  error={errorMessage !== ""}
                  errorText={errorMessage}
                  uploadedText=""
                />
              )}
            </MotifFileUploader>
          )}
        </MotifFormField>
      </MotifModalBody>
      {uploadProgress === 0 && (
        <MotifModalFooter>
          <MotifButton
            onClick={closeImportModal}
            variant="secondary"
            hidden={isUploading || isUploadSuccess}
          >
            Cancel
          </MotifButton>
          <MotifButton
            disabled={!file || isUploading || errorMessage !== ""}
            onClick={handleUpload}
            hidden={isUploading || isUploadSuccess}
          >
            Upload
          </MotifButton>
        </MotifModalFooter>
      )}
    </MotifModal>
  );
};
export default ConsumptionImport;
